import { AppDispatch } from '../../redux/store';
import DeleteFolderModal from './modal/delete_folder';
import { dragFileToFolderThunk } from '../../redux/thunks/file';
import { FolderObject } from '../../params/redux/folder';
import { MoreVert } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import React, { useEffect, useRef, useState } from 'react';
import { settingSelectedFolder, settingSelectedFolderForEdit } from '../../redux/slices/folder';

interface FolderItemProps {
    folder: FolderObject;
    setUploadFile: (value: boolean) => void;
    isAssigned?: boolean
}

const FolderItemComponent = ({ folder, setUploadFile, isAssigned = false }: FolderItemProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const [deleteFolder, setDeleteFolder] = useState<boolean>(false);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    // Refs for the folder and menu components
    const folderRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Close the menu if clicked outside
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                folderRef.current &&
                !folderRef.current.contains(event.target as Node) &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setAnchorEl(null);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const selectFolderHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(settingSelectedFolder(folder));
    };

    const uploadFileHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setUploadFile(true);
    };

    const deleteFolderModalHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteFolder(true);
    };

    const backTappingHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleMenuClose();
    };

    const editFolderHandler = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(settingSelectedFolderForEdit(folder));
    };

    const handleFileMoveToFolder = async (fileId: number, folderId: number) => {
        try {
            await dispatch(dragFileToFolderThunk({
                fileId,
                folderId,
                client_id: parseInt(id as string, 10)
            })).unwrap();
        } catch (e) {
            //
        }
    };

    return (
        <Box
            ref={folderRef}
            height="100%"
            minWidth="250px"
            maxWidth="250px"
            bgcolor={isDragOver ? brand[100] : gray[50]}
            borderRadius="5px"
            border={isDragOver ? `2px dashed ${brand[300]}` : `1px solid ${gray[200]}`}
            onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            sx={{
                'transition': 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: brand[100],
                    cursor: 'pointer'
                }
            }}
            onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const fileId = e.dataTransfer.getData('fileId');
                if (fileId) {
                    // Move file to this folder
                    handleFileMoveToFolder(Number(fileId), folder.id);
                }
            }}
            onClick={backTappingHandler}
        >
            <Box display="flex" justifyContent="center" alignItems="center" maxHeight="18vh" minHeight="18vh" onClick={selectFolderHandler}>
                {isDragOver && <Typography variant="body2" color="primary" fontWeight="bold">Drop file here</Typography>}
                {!isAssigned && folder.file_count === 0 && !isDragOver && <Button variant="outlined" color="secondary" onClick={uploadFileHandler}>Upload File</Button>}
            </Box>
            <Box minHeight="8vh" maxHeight="8vh" paddingLeft={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" maxWidth="200px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" onClick={selectFolderHandler}>
                        <b>{folder.name}</b>
                    </Typography>
                    {!isAssigned && <IconButton onClick={handleButtonClick}>
                        <MoreVert fontSize="small" />
                    </IconButton>}
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': `folder-${folder.id}`
                        }}
                        ref={menuRef}
                    >
                        <MenuItem onClick={editFolderHandler}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Edit</Box>
                        </MenuItem>
                        <MenuItem onClick={deleteFolderModalHandler}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">Delete</Box>
                        </MenuItem>
                    </Menu>
                </Box>
                <Typography variant="body2" color="textSecondary">
                    {folder.file_count} Files . {folder.file_size}
                </Typography>
            </Box>
            <DeleteFolderModal open={deleteFolder} onClose={() => { setDeleteFolder(false); setAnchorEl(null); }} folder={folder} />
        </Box>
    );
};

export default FolderItemComponent;
